/**
 * The entry card on both company profiles.
 *
 * ONE CARD, TWO AUDIENCES, AND A HARD LINE BETWEEN THEM. A founder sees what is
 * blocking generation and what to do about it. An investor sees either "View
 * strategic analysis" or "Strategic analysis not available yet" — and never a
 * readiness state, a missing field, a candidate, or a question, because all of
 * those describe founder-private material about a company that is not theirs.
 *
 * `canEdit` decides which. It comes from server-backed membership, never from a
 * route parameter or a prop a caller found convenient.
 *
 * The card does not become a navigation destination. It sits inside the profile
 * it describes and opens one shared detail route.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { ChevronRight, ScanSearch } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import type { ServerReadiness } from '@/lib/strategic-analysis/types';

export type EntryState =
  | 'setup_required'
  | 'not_generated'
  | 'processing'
  | 'ready'
  | 'sources_changed'
  | 'stale'
  | 'unavailable';

export interface StrategicAnalysisEntryProps {
  /** Server-backed active team membership. Never a route parameter. */
  canEdit: boolean;
  /** At least one canonical report exists and the viewer may read it. */
  hasReport: boolean;
  /** Founder-only. Absent for an investor by construction. */
  readiness?: ServerReadiness | null;
  /** Raw profile/deck/interview/data-room sources moved since the last approval. */
  sourcesChanged?: boolean;
  isProcessing?: boolean;
  isStale?: boolean;
  onOpen: () => void;
  onCompleteInputs?: () => void;
}

/**
 * The state machine, extracted so it can be asserted without rendering.
 *
 * An investor collapses to exactly two outcomes: there is a report, or there is
 * not. Every founder-only state above them would otherwise leak readiness.
 */
export function resolveEntryState(props: {
  canEdit: boolean;
  hasReport: boolean;
  readiness?: ServerReadiness | null;
  sourcesChanged?: boolean;
  isProcessing?: boolean;
  isStale?: boolean;
}): EntryState {
  if (!props.canEdit) return props.hasReport ? 'ready' : 'unavailable';
  if (props.isProcessing) return 'processing';
  // A REPORT THAT EXISTS OUTRANKS A SETUP PROMPT. `readiness` here is the FIRST
  // blocking analysis of five, and a founder is routinely mid-way: Competition
  // and Porter ready, PESTEL still wanting two fields. Prompting for setup
  // regardless made the card claim "the analyses can run" was still pending
  // while eight finished reports sat behind it, unreachable — the card offers
  // one action, and it was spending it on the wrong one.
  //
  // Setup is what you show when there is nothing to read. The detail screen
  // resolves readiness per TAB, so the per-analysis prompt survives; it just
  // lives where the analysis it describes is.
  if (!props.hasReport && props.readiness && props.readiness.status !== 'ready') {
    return 'setup_required';
  }
  if (props.sourcesChanged) return 'sources_changed';
  if (!props.hasReport) return 'not_generated';
  if (props.isStale) return 'stale';
  return 'ready';
}

const COPY: Record<EntryState, { title: string; body: string; action: string | null }> = {
  setup_required: {
    title: 'Strategic analysis',
    body: 'A few inputs still need your review before the analyses can run.',
    action: 'Complete analysis inputs',
  },
  not_generated: {
    title: 'Strategic analysis',
    body: 'Competition, Five Forces, PESTEL, Market Validation and Exit Strategy — researched from public sources, with every statement linked to its evidence.',
    action: 'Generate analyses',
  },
  processing: {
    title: 'Strategic analysis',
    body: 'An analysis is running. You can leave this page; it keeps going.',
    action: 'View progress',
  },
  ready: {
    title: 'Strategic analysis',
    body: 'Competition, Five Forces, PESTEL, Market Validation and Exit Strategy, with the evidence behind each one.',
    action: 'View strategic analysis',
  },
  sources_changed: {
    title: 'Strategic analysis',
    body: 'Your profile, deck, interview or data room changed. Review the updated values — nothing in a published analysis changes until you approve them.',
    action: 'Review updated sources',
  },
  stale: {
    title: 'Strategic analysis',
    body: 'Your inputs changed after this analysis ran. It is still a true record of what was assessed.',
    action: 'Refresh analysis',
  },
  unavailable: {
    title: 'Strategic analysis',
    // Deliberately says nothing about why. "The founder has not completed their
    // inputs" is founder-private information about someone else's company.
    body: 'Strategic analysis not available yet.',
    action: null,
  },
};

export function StrategicAnalysisEntry(props: StrategicAnalysisEntryProps) {
  const colors = useThemeColors();
  const state = resolveEntryState(props);
  const copy = COPY[state];

  const onPress = state === 'setup_required' && props.onCompleteInputs
    ? props.onCompleteInputs
    : props.onOpen;

  return (
    <View
      className="rounded-2xl p-4 gap-3"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-center gap-2.5">
        <View
          className="items-center justify-center rounded-xl shrink-0"
          style={{ width: 36, height: 36, backgroundColor: colors.primary[50] }}
        >
          <ScanSearch size={19} color={colors.primary[600]} />
        </View>
        <Text numberOfLines={1} className="flex-1 text-base font-semibold" style={{ color: colors.text.primary }}>
          {copy.title}
        </Text>
        {state === 'stale' && (
          <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: colors.amber.light }}>
            <Text numberOfLines={1} className="text-xs font-semibold" style={{ color: colors.amber.main }}>
              Inputs changed
            </Text>
          </View>
        )}
      </View>

      <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
        {copy.body}
      </Text>

      {copy.action && (
        <View
          className="rounded-xl overflow-hidden"
          style={{
            height: 44,
            backgroundColor: state === 'ready' ? colors.surface.secondary : colors.primary[600],
          }}
        >
          <AnimatedPressable
            onPress={onPress}
            accessibilityLabel={copy.action}
            className="flex-row items-center justify-center gap-1.5"
            style={{ flex: 1 }}
          >
            <Text
              numberOfLines={1}
              className="text-sm font-semibold"
              style={{ color: state === 'ready' ? colors.primary[700] : colors.text.inverse }}
            >
              {copy.action}
            </Text>
            <ChevronRight size={16} color={state === 'ready' ? colors.primary[700] : colors.text.inverse} />
          </AnimatedPressable>
        </View>
      )}
    </View>
  );
}
